import { useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Crown, Check, Shield, CreditCard, Copy, Key, Download } from "lucide-react";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PLAN_DETAILS = {
  starter: { name: "Starter", price: "$29", sites: "1 Website", yearly: "/year" },
  business: { name: "Business", price: "$58", sites: "3 Websites", yearly: "/year" },
  agency: { name: "Agency", price: "$118", sites: "7 Websites", yearly: "/year" },
  enterprise: { name: "Enterprise", price: "$299", sites: "Unlimited", yearly: "/year" },
};

export default function CheckoutModal({ open, onOpenChange, planId, apiUrl, onSuccess }) {
  const [status, setStatus] = useState("idle"); // idle | processing | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [licenseExpiry, setLicenseExpiry] = useState("");
  const [copied, setCopied] = useState(false);
  const plan = PLAN_DETAILS[planId] || PLAN_DETAILS.starter;

  const handleClose = () => {
    if (status === "processing") return;
    onOpenChange(false);
    setTimeout(() => {
      setStatus("idle");
      setErrorMsg("");
      setLicenseKey("");
      setLicenseExpiry("");
      setCopied(false);
    }, 300);
  };

  const copyLicense = () => {
    navigator.clipboard.writeText(licenseKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden" data-testid="checkout-modal">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="font-display font-bold text-xl text-[#09090B] flex items-center gap-2">
            {status === "success" ? (
              <><Key size={20} className="text-[#067d06]" /> Purchase Complete</>
            ) : (
              <><Crown size={20} className="text-[#FF7300]" /> Checkout — PRO {plan.name}</>
            )}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#52525B]">
            {status === "success"
              ? "Your license key is ready. Save it — you'll need it to activate the plugin."
              : "Complete your purchase to download the PRO plugin."}
          </DialogDescription>
        </DialogHeader>

        <Separator />

        <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Order Summary */}
          <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-3" data-testid="checkout-order-summary">
            <div className="text-xs font-bold text-[#A1A1AA] uppercase tracking-wider">Order Summary</div>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm font-semibold text-[#09090B]">
                  Affiliate PRO — {plan.name}
                </div>
                <div className="text-xs text-[#52525B]">{plan.sites} license &middot; 1 year</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-[#09090B]">{plan.price}</div>
                <div className="text-[10px] text-[#A1A1AA]">{plan.yearly}</div>
              </div>
            </div>
          </div>

          {/* Success State — License Key */}
          {status === "success" && (
            <div data-testid="checkout-success">
              <div className="bg-[#e7f7e7] border border-[#067d06]/20 rounded-lg p-5 text-center mb-4">
                <div className="w-12 h-12 rounded-full bg-[#067d06]/10 flex items-center justify-center mx-auto mb-3">
                  <Check size={24} className="text-[#067d06]" />
                </div>
                <div className="text-sm font-bold text-[#067d06] mb-1">Payment Successful!</div>
                <p className="text-xs text-[#52525B]">A confirmation email has been sent to your PayPal email.</p>
              </div>

              {/* License Key Display */}
              <div className="border border-[#FF7300]/30 rounded-lg p-4 bg-[#FF7300]/[0.03]" data-testid="license-key-display">
                <div className="flex items-center gap-2 mb-2">
                  <Key size={14} className="text-[#FF7300]" />
                  <span className="text-xs font-bold text-[#FF7300] uppercase tracking-wider">Your License Key</span>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-[#E4E4E7] rounded-md px-3 py-2.5 text-sm font-mono font-bold text-[#09090B] tracking-wide" data-testid="license-key-value">
                    {licenseKey}
                  </code>
                  <button
                    onClick={copyLicense}
                    className="flex items-center gap-1.5 px-3 py-2.5 bg-[#09090B] text-white text-xs font-semibold rounded-md hover:bg-[#27272A] transition-colors cursor-pointer border-0"
                    data-testid="copy-license-btn"
                  >
                    <Copy size={12} />
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
                {licenseExpiry && (
                  <p className="text-[10px] text-[#A1A1AA] mt-2">Valid until {licenseExpiry.slice(0, 10)}</p>
                )}
              </div>

              {/* Download PRO Plugin */}
              <button
                onClick={() => onSuccess && onSuccess(planId)}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-[#FF7300] text-white rounded-md py-3 text-sm font-bold hover:bg-[#E66800] transition-colors cursor-pointer border-0"
                data-testid="checkout-download-pro-btn"
              >
                <Download size={16} /> Download PRO Plugin
              </button>

              {/* 30-day guarantee note */}
              <div className="mt-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg p-3 flex items-start gap-2">
                <Shield size={14} className="text-[#FF7300] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#9a3412]">
                  <strong>30-Day Money-Back Guarantee:</strong> Not happy? Contact{" "}
                  <a href="mailto:info@amznawp.com" className="underline">info@amznawp.com</a>{" "}
                  within 30 days for a full refund.{" "}
                  <Link to="/refund-policy" className="underline font-semibold" onClick={handleClose}>Read policy</Link>
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {status === "error" && (
            <div className="bg-[#fee2e2] border border-[#b12704]/20 rounded-lg p-3 text-center" data-testid="checkout-error">
              <div className="text-sm font-semibold text-[#b12704]">Payment Failed</div>
              <p className="text-xs text-[#52525B] mt-1">{errorMsg || "Something went wrong. Please try again."}</p>
            </div>
          )}

          {/* PayPal Buttons */}
          {status !== "success" && (
            <div data-testid="paypal-buttons-container">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard size={14} className="text-[#52525B]" />
                <span className="text-xs font-semibold text-[#52525B]">Pay with PayPal or Credit/Debit Card</span>
              </div>
              {!PAYPAL_CLIENT_ID ? (
                <div className="bg-[#fee2e2] border border-[#b12704]/20 rounded-lg p-4 text-center" data-testid="paypal-config-error">
                  <div className="text-sm font-semibold text-[#b12704]">Payment Unavailable</div>
                  <p className="text-xs text-[#52525B] mt-1">Payment system is being configured. Please contact <a href="mailto:info@amznawp.com" className="underline text-[#FF7300]">info@amznawp.com</a> for assistance.</p>
                </div>
              ) : (
              <PayPalScriptProvider
                options={{
                  clientId: PAYPAL_CLIENT_ID,
                  currency: "USD",
                  intent: "capture",
                }}
              >
                <PayPalButtons
                  style={{
                    layout: "vertical",
                    color: "gold",
                    shape: "rect",
                    label: "pay",
                    height: 45,
                  }}
                  disabled={status === "processing"}
                  createOrder={async () => {
                    setStatus("processing");
                    setErrorMsg("");
                    try {
                      const res = await fetch(`${apiUrl}/payment/create-order`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ plan_id: planId }),
                      });
                      if (!res.ok) throw new Error("Failed to create order");
                      const data = await res.json();
                      return data.id;
                    } catch (err) {
                      setStatus("error");
                      setErrorMsg("Could not initiate payment. Please try again.");
                      throw err;
                    }
                  }}
                  onApprove={async (data) => {
                    try {
                      const res = await fetch(`${apiUrl}/payment/capture-order`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ order_id: data.orderID }),
                      });
                      if (!res.ok) throw new Error("Failed to capture payment");
                      const result = await res.json();
                      if (result.status === "COMPLETED") {
                        setLicenseKey(result.license_key || "");
                        setLicenseExpiry(result.license_expiry || "");
                        setStatus("success");
                      } else {
                        setStatus("error");
                        setErrorMsg("Payment was not completed. Please try again.");
                      }
                    } catch (err) {
                      setStatus("error");
                      setErrorMsg("Payment capture failed. Please contact support.");
                    }
                  }}
                  onCancel={() => {
                    setStatus("idle");
                  }}
                  onError={() => {
                    setStatus("error");
                    setErrorMsg("Payment error. Please try again or use a different method.");
                  }}
                />
              </PayPalScriptProvider>
              )}

              {/* 30-day guarantee badge */}
              <div className="mt-3 flex items-center justify-center gap-2 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg px-3 py-2" data-testid="guarantee-badge">
                <Shield size={14} className="text-[#FF7300]" />
                <span className="text-xs font-semibold text-[#9a3412]">30-Day Money-Back Guarantee</span>
              </div>
            </div>
          )}

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-4 pt-2">
            <div className="flex items-center gap-1.5 text-[10px] text-[#A1A1AA]">
              <Shield size={12} /> Secure Payment
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-[#A1A1AA]">
              <Check size={12} /> Instant Download
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-[#A1A1AA]">
              <Crown size={12} /> 1 Year License
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
